import { Component, ElementRef, Input, NgZone, OnInit, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GeolocationInfo } from 'src/app/interfaces/geolocation';
import { GoogleMapsService } from 'src/app/services/google-maps/google.maps.service';

@Component({
  selector: 'app-map-search-modal',
  templateUrl: './map-search-modal.component.html',
  styleUrls: ['./map-search-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapSearchModalComponent implements OnInit {
  @Input() title: string;
  @Input() latitude: number;
  @Input() longitude: number;

  @ViewChild('searchPlaces') searchElementRef: ElementRef;

  search: GeolocationInfo = {
    latitude: 54.11815748515591,
    longitude: -2.541813085952347,
  };
  marker: GeolocationInfo = {
    latitude: 54.11815748515591, 
    longitude: -2.541813085952347,
  };
  zoom = 5;
  address: string;
  loading = false;
  mapsReady = false;
  suggestions: string[] = [];
  mapOptions: google.maps.MapOptions = {
    zoomControl: false, // Hide zoom controls
    cameraControl: true, // Show camera controls
    mapTypeControl: false, // Hide map type control (Map/Satellite)
    streetViewControl: false, // Hide street view control
    fullscreenControl: false, // Hide fullscreen control
    gestureHandling: 'greedy' // Optional: allows all gestures on the map
  };
  
  constructor(
    public activeModal: NgbActiveModal,
    private ngZone: NgZone,
    private googleMapsService: GoogleMapsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.latitude && this.longitude) {
      this.search = {
        latitude: this.latitude,
        longitude: this.longitude,
      };
      this.marker = { ...this.search }; // Initial marker position
    }

    // The Maps script isn't loaded eagerly on app start anymore, so load it
    // only when this modal is actually opened, then wire up the autocomplete.
    this.googleMapsService
      .loadGoogleMapsApi()
      .then(() => {
        this.ngZone.run(() => {
          this.mapsReady = true;
          this.cdr.detectChanges();
          this.initAutocomplete();
        });
      })
      .catch((err) => console.error(err));
  }

  private initAutocomplete(): void {
    const autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, {
      types: ['geocode'],
      componentRestrictions: { country: 'gb' },
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        this.ngZone.run(() => {
          this.marker.latitude = place.geometry.location.lat();
          this.marker.longitude = place.geometry.location.lng();
          this.address = place.formatted_address;
        });
      }
    });
  }

  updateMarker(event: { lat: number, lng: number }) {
    if (!event.lat && !event.lng) {
      return
    }
    let lat: number = event.lat;
    let lng: number = event.lng;
    
    if (typeof lat === 'number' && typeof lng === 'number') {
      this.marker = {
        latitude: lat,
        longitude: lng,
      };
  
      this.ngZone.run(() => {
        this.loading = true;
      });
  
      this.googleMapsService.geocodeLatLng(this.marker.latitude, this.marker.longitude)
        .then((results) => {
          this.ngZone.run(() => {
            if (results && results[0]) {
              this.address = results[0].formatted_address;
            } else {
              this.address = null;
            }
            this.loading = false;
            this.cdr.detectChanges();
          });
        })
        .catch(() => {
          this.ngZone.run(() => {
            this.address = null;
            this.loading = false;
            this.cdr.detectChanges();
          });
        });
    } else {
      console.error('Invalid lat/lng values:', lat, lng);
    }
  }

  select(): void {
    this.activeModal.close({
      location: this.marker,
      address: this.address,
    });
  }

  doSearch(): void {
    if (this.address) {
      this.googleMapsService.getSuggestions(this.address).then(suggestions => {
        if (suggestions && suggestions.length > 0) {
          const firstSuggestion = suggestions[0];
          const placeId = firstSuggestion.place_id;

          this.googleMapsService.getPlaceDetails(placeId).then(placeDetails => {
            if (placeDetails && placeDetails.geometry) {
              const location = placeDetails.geometry.location;
              this.updateMarker({
                lat: location.lat(),
                lng: location.lng(),
              });
              this.address = placeDetails.formatted_address;
            }
          }).catch(error => {
            console.error('Error fetching place details:', error);
          });
        }
      }).catch(error => {
        console.error('Error during search:', error);
      });
    }
  }

  mapReady(googleMap: google.maps.Map) {
    if (googleMap) {
      const initialLatLng = new google.maps.LatLng(this.latitude, this.longitude);
      this.updateMarker({ lat: initialLatLng.lat(), lng: initialLatLng.lng() });
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(initialLatLng);
      googleMap.fitBounds(bounds);
      
      setTimeout(() => {
        googleMap.setZoom(16);
      }, 100); 
  
      const searchBox = new google.maps.places.SearchBox(this.searchElementRef.nativeElement);
  
      googleMap.addListener('bounds_changed', () => {
        searchBox.setBounds(googleMap.getBounds() as google.maps.LatLngBounds);
      });
  
      searchBox.addListener('places_changed', () => {
        const places = searchBox.getPlaces();
        if (places.length === 0) {
          return;
        }
        const bounds = new google.maps.LatLngBounds();
  
        if (!places[0].geometry) {
          return;
        }
  
        if (places[0].geometry.viewport) {
          bounds.union(places[0].geometry.viewport);
        } else {
          bounds.extend(places[0].geometry.location);
        }
  
        const placeLatLng = places[0].geometry.location;
        this.updateMarker({ lat: placeLatLng.lat(), lng: placeLatLng.lng() });
  
        googleMap.fitBounds(bounds);
        this.zoom = 16;
        googleMap.setZoom(this.zoom);
  
        this.ngZone.run(() => {
          this.address = places[0].formatted_address;
        });
      });
  
      googleMap.addListener('click', (event: google.maps.MapMouseEvent) => {
        this.updateMarker({ lat: event.latLng?.lat(), lng: event.latLng?.lng() });
      });
    } else {
      console.error('Map object not initialized.');
    }
  }
  
}
